<template>
  <div
    role="main"
    aria-label="Détails du gouvernorat"
    tabindex="-1"
  >
    <b-card
      v-if="governorate"
      no-body
      aria-labelledby="gov-title"
      aria-describedby="gov-desc"
      role="region"
      class="mb-2"
    >
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h3
              id="gov-title"
              class="card-title"
            >
              {{ governorate.name }}
            </h3>
            <span
              id="gov-desc"
              class="sr-only"
            >Informations détaillées sur le gouvernorat, y compris délégations, localités, commandes et statut logistique.</span>
          </div>
          <div class="ml-5">
            <b-button
              v-if="user.role === 'admin'"
              :variant="governorate.is_active ? 'outline-danger' : 'outline-success'"
              :aria-pressed="governorate.is_active ? 'true' : 'false'"
              :aria-label="governorate.is_active ? 'Désactiver ce gouvernorat pour la logistique' : 'Activer ce gouvernorat pour la logistique'"
              @click="toggleStatus"
            >
              {{ governorate.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
            <b-badge
              :variant="governorate.is_active ? 'danger' : 'success'"
              class="ml-2"
              :aria-label="governorate.is_active ? 'Statut logistique : activé' : 'Statut logistique : désactivé'"
            >
              {{ governorate.is_active ? 'Activé' : 'Désactivé' }}
            </b-badge>
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col
            lg="5"
            md="6"
            sm="12"
          >
            <b-card
              title="Informations territoriales"
              aria-label="Informations territoriales du gouvernorat"
            >
              <b-list-group flush>
                <b-list-group-item>
                  <strong>Code géographique&nbsp;:</strong> <span aria-label="Code du gouvernorat">{{ governorate.code }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nom officiel&nbsp;:</strong> <span aria-label="Nom du gouvernorat">{{ governorate.name }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nombre de délégations&nbsp;:</strong> <span aria-label="Nombre de délégations logistiques">{{ governorate.num_delegations }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nombre de localités&nbsp;:</strong> <span aria-label="Nombre de localités desservies">{{ governorate.num_localities }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nombre de commandes&nbsp;:</strong> <span aria-label="Nombre total de commandes logistiques">{{ governorate.num_orders }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>État logistique&nbsp;:</strong> <span aria-label="Statut logistique">{{ governorate.is_active ? 'Actif' : 'Inactif' }}</span>
                </b-list-group-item>
              </b-list-group>
              <span class="sr-only">Utilisez les boutons d’action pour modifier ou changer le statut logistique du gouvernorat.</span>
            </b-card>
          </b-col>

          <b-col
            lg="7"
            md="6"
            sm="12"
          >
            <b-card
              title="Délégations logistiques"
              aria-label="Liste des délégations logistiques"
            >
              <div class="mb-2">
                <b-form-input
                  v-model="delegationSearch"
                  placeholder="Rechercher une délégation logistique..."
                  class="mb-2"
                  aria-label="Recherche de délégation logistique"
                />
                <span class="sr-only">Filtrez la liste des délégations logistiques par nom.</span>
              </div>
              <div v-if="filteredDelegations.length > 0">
                <b-table
                  :items="filteredDelegations"
                  :fields="delegationFields"
                  responsive="sm"
                  show-empty
                  aria-label="Tableau des délégations logistiques"
                  role="table"
                >
                  <template #cell(is_active)="data">
                    <b-badge
                      :variant="data.item.is_active ? 'danger' : 'success'"
                      :aria-label="data.item.is_active ? 'Délégation active' : 'Délégation inactive'"
                    >
                      {{ data.item.is_active ? 'Actif' : 'Inactif' }}
                    </b-badge>
                  </template>
                  <template #cell(actions)="data">
                    <router-link
                      :to="{ name: 'delegation-detail', params: { id: data.item.id }}"
                      class="btn btn-sm btn-outline-primary"
                      :aria-label="`Voir les détails de la délégation logistique ${data.item.name}`"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="14"
                      />
                    </router-link>
                  </template>
                </b-table>
              </div>
              <div
                v-else
                class="text-center"
                aria-live="polite"
              >
                <p>Aucune délégation logistique trouvée</p>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col cols="12">
            <b-card
              title="Statistiques logistiques"
              aria-label="Statistiques logistiques du gouvernorat"
            >
              <apexchart
                v-if="chartOptions && chartSeries"
                type="bar"
                height="350"
                :options="chartOptions"
                :series="chartSeries"
                aria-label="Graphique des statistiques logistiques"
              />
              <span class="sr-only">Ce graphique présente le nombre de délégations, localités et commandes pour ce gouvernorat.</span>
            </b-card>
          </b-col>
        </b-row>

        <!-- Replace existing table with the new component -->
        <orders-table
          v-if="governorate.recent_orders && governorate.recent_orders.length > 0"
          :orders="governorate.recent_orders"
          :total-orders="governorate.num_orders"
          :entity-id="governorate.id"
          entity-type="governorate"
          card-title="Dernières commandes logistiques"
          class="mt-3"
          aria-label="Tableau des dernières commandes logistiques pour ce gouvernorat"
        />

        <b-row class="mt-2">
          <b-col cols="12">
            <b-button
              v-if="isAdmin"
              variant="primary"
              class="mr-1"
              :to="{ name: 'governorate-edit', params: { id: $route.params.id }}"
              aria-label="Modifier les informations territoriales du gouvernorat"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-50"
                size="16"
              />
              <span>Modifier</span>
            </b-button>
            <b-button
              variant="outline-primary"
              :to="{ name: 'governorate-list' }"
              aria-label="Retour à la liste des gouvernorats"
            >
              <feather-icon
                icon="ListIcon"
                class="mr-50"
                size="16"
              />
              <span>Liste des gouvernorats</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <div
      v-else
      class="text-center p-5"
      aria-live="polite"
      role="status"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        Chargement des détails du gouvernorat pour la logistique...
      </p>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BListGroup,
  BListGroupItem,
  BBadge,
  BButton,
  BSpinner,
  BTable,
  BFormInput,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VueApexCharts from 'vue-apexcharts'
import storeAuth from '@/store/store'
import OrdersTable from '@/components/tables/OrdersTable.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
    BSpinner,
    BTable,
    BFormInput,
    apexchart: VueApexCharts,
    OrdersTable,
  },
  data() {
    return {
      governorate: null,
      isLoading: false,
      user: {},
      delegationSearch: '',
      delegationFields: [
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Nom' },
        { key: 'is_active', label: 'État' },
        { key: 'actions', label: 'Actions' },
      ],
      orderFields: [
        { key: 'id', label: 'ID' },
        { key: 'customer_name', label: 'Client' },
        { key: 'total_amount', label: 'Montant total' },
        { key: 'status', label: 'Statut' },
        { key: 'actions', label: 'Actions' },
      ],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 10,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent'],
        },
        xaxis: {
          categories: ['Délégations', 'Localités', 'Commandes'],
        },
        yaxis: {
          title: {
            text: 'Nombre',
          },
        },
        fill: {
          opacity: 1,
          colors: ['#7367F0', '#00CFE8', '#EA5455'],
        },
        tooltip: {
          y: {
            formatter: val => `${val}`,
          },
        },
      },
      chartSeries: [],
    }
  },
  computed: {
    isAdmin() {
      return this.user.role === 'admin'
    },
    filteredDelegations() {
      if (!this.governorate || !this.governorate.delegations) return []

      return this.delegationSearch
        ? this.governorate.delegations.filter(d => d.name.toLowerCase().includes(this.delegationSearch.toLowerCase()))
        : this.governorate.delegations
    },
  },
  created() {
    this.fetchGovernorateDetails()
    this.user = storeAuth.state.user
  },
  methods: {
    async fetchGovernorateDetails() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/governorates/detail/${this.$route.params.id}/`)
        // Ensure is_active is properly cast to boolean
        this.governorate = {
          ...data,
          is_active: Boolean(data.is_active),
        }

        // Setup chart data
        this.chartSeries = [{
          name: 'Statistiques',
          data: [
            this.governorate.num_delegations || 0,
            this.governorate.num_localities || 0,
            this.governorate.num_orders || 0,
          ],
        }]

        this.isLoading = false
      } catch (error) {
        console.error('Error fetching governorate details:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des détails du gouvernorat')
        this.isLoading = false
      }
    },
    async toggleStatus() {
      try {
        // Show confirmation dialog before proceeding
        const result = await this.$swal({
          title: this.governorate.is_active ? 'Désactiver le gouvernorat?' : 'Activer le gouvernorat?',
          text: this.governorate.is_active
            ? 'Cette action empêchera les livraisons dans cette région. Êtes-vous sûr?'
            : 'Cette action permettra les livraisons dans cette région. Êtes-vous sûr?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, confirmer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        // If user confirmed the action
        if (result.value) {
          const { data } = await axios.post(`/api/governorates/toggle-status/${this.$route.params.id}/`)
          this.governorate.is_active = data.is_active
          this.showToast('success', 'top-center',
            `Le gouvernorat a été ${data.is_active ? 'activé' : 'désactivé'} avec succès`)
        }
      } catch (error) {
        console.error('Error toggling governorate status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      }
    },
    viewAllOrders() {
      // Navigate to orders list with governorate filter
      this.$router.push({
        name: 'order-list',
        query: { governorate: this.$route.params.id },
      })
    },
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>
